import { styled } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RemoveCircle, CheckCircle } from '@material-ui/icons';
import { useDomains, useRefreshDomain } from '../../hooks/domains';
import useToggle from '../../hooks/useToggle';
import formatDate from '../../helpers/formatDate';
import InnerHeader from '../../component/InnerHeader';
import NoxTable from '../../component/Table';
import DomainTransferForm from '../../component/forms/DomainTransferForm';
import { deleteDomain } from '../../services/domain';

const Wrapper = styled('div')({
  height: 'calc(100vh-10.5rem)'
});

const transformData = (domains) => {
  if (!domains || !domains.length) {
    return [];
  }
  return domains.map(({ _id, name, ns, managedBy, status, updatedAt }, idx) => {
    const formattedDate = formatDate(updatedAt);
    return {
      _id,
      key: idx,
      managedBy,
      name,
      ns,
      status,
      updatedAt: formattedDate.toString() === 'Inv' ? '-' : formattedDate
    };
  });
};

const Domains = () => {
  const { handleOpen, handleClose, open } = useToggle();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const currentProject = useSelector(({ project }) => project.currentProject);
  const { mutate: refreshDomainMutation, isLoading: refreshingDomain } = useRefreshDomain();
  const { data, isLoading, isFetching } = useDomains(currentProject._id);

  const refreshDomain = () => {
    selectedRowKeys.forEach((idx) => {
      const item = data?.domains[idx];
      refreshDomainMutation({ domainId: item._id, projectId: currentProject._id });
    });
  };

  const onDomainDelete = async () => {
    await Promise.all(
      selectedRowKeys.map(async (index) => {
        const item = data.domains[index];
        await deleteDomain(currentProject._id, item._id);
        setSelectedRowKeys([]);
        refreshDomain();
      })
    );
  };

  const columns = useMemo(
    () => [
      {
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name - b.name,
        title: 'Name'
      },
      {
        dataIndex: 'ns',
        key: 'ns',
        render: (item) => (
          <div>
            {item.map((ns, idx) => (
              <div key={idx}>{ns}</div>
            ))}
          </div>
        ),
        sorter: (a, b) => a.name.toString().localeCompare(b.name),
        title: 'NS'
      },
      {
        dataIndex: 'managedBy',
        key: 'managedBy',
        sorter: (a, b) => a.managedBy.toString().localeCompare(b.managedBy),
        title: 'Managed By'
      },
      {
        dataIndex: 'status',
        key: 'status',
        render: (item) => (
          <div>
            {item === 'active' ? (
              <CheckCircle style={{ color: 'green' }} />
            ) : (
              <RemoveCircle style={{ color: 'red' }} />
            )}
          </div>
        ),
        sorter: (a, b) => a.status - b.status,
        title: 'Status'
      },
      {
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        sorter: (a, b) => a.updatedAt - b.updatedAt,
        title: 'Updated At'
      }
    ],
    []
  );

  const actions = [
    {
      onClick: handleOpen,
      title: 'Transfer Domain',
      type: 'primary'
    }
  ];

  if (selectedRowKeys.length) {
    actions.unshift(
      {
        onClick: refreshDomain,
        title: 'Refresh',
        type: 'secondary'
      },
      {
        onClick: onDomainDelete,
        title: 'Delete',
        type: 'secondary'
      }
    );
  }

  const transformedData = useMemo(() => transformData(data?.domains), [data?.domains]);

  return (
    <Wrapper>
      <InnerHeader title="Domains" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={transformedData}
        loading={isLoading || isFetching || refreshingDomain}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <DomainTransferForm
        currentProject={currentProject._id}
        handleClose={handleClose}
        open={open}
      />
    </Wrapper>
  );
};

export default Domains;
